import { formatDurationMinutes, formatTime } from "@/hooks/use-formatter.hook.ts";
import { useDeleteTimeEntry, useEmployeeTimeEntries } from "@/queries/use-time-tracking.query.ts";
import analyticsService, { analyticEvents } from "@/services/analytics-service.ts";
import { ColumnSizes } from "@/types/common.types.ts";
import { ApiTimeEntry } from "@/types/time-tracking.types.ts";
import {
  Button,
  ConfirmationModal,
  DropdownMenu,
  DropdownMenuItem,
  FontAwesomeIcon,
  Modal,
  ModalProps,
  Stack,
  Table,
  Text,
  useModals,
} from "@skailer-com/uikit";
import { ColumnDef, createColumnHelper, Row } from "@tanstack/react-table";
import { format } from "date-fns";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { TimeEntryEditModal } from "./TimeEntryEditModal.tsx";

function entryDurationMinutes(entry: ApiTimeEntry): number | null {
  if (!entry.datetime_clocked_out) return null;
  return Math.floor(
    (entry.datetime_clocked_out.getTime() - entry.datetime_clocked_in.getTime()) / 60000
  );
}

export interface AdjustTimeModalProps extends ModalProps {
  employeeId: number;
  date: Date;
  canEdit: boolean;
}

const columnHelper = createColumnHelper<ApiTimeEntry>();

export const AdjustTimeModal = ({ employeeId, date, canEdit, ...props }: AdjustTimeModalProps) => {
  const { t } = useTranslation();
  const { close, openModal } = useModals();

  const { data, isLoading } = useEmployeeTimeEntries(
    employeeId,
    { date_period_start: date, date_period_end: date, page_number: 1 },
    true
  );
  const deleteEntry = useDeleteTimeEntry();
  const entries = useMemo(() => data?.items ?? [], [data]);

  const handleEdit = useCallback(
    (entry: ApiTimeEntry) => {
      openModal(TimeEntryEditModal, { entry });
    },
    [openModal]
  );

  const handleCreate = () => {
    openModal(TimeEntryEditModal, { entry: null, employeeId, date });
  };

  const handleDelete = useCallback(
    (entry: ApiTimeEntry) => {
      openModal(ConfirmationModal, {
        question: t("clocking:delete_entry_confirm"),
        acceptTitle: t("clocking:delete_entry"),
        onAccept: async () => {
          await deleteEntry.mutateAsync(entry.id);
          analyticsService.trackEvent(analyticEvents.timeTracking.entryDeleted);
          if (entries.length <= 1) close(props.id);
        },
      });
    },
    [t, openModal, deleteEntry, entries, close, props.id]
  );

  const columns = useMemo<ColumnDef<ApiTimeEntry, unknown>[]>(() => {
    const cols: ColumnDef<ApiTimeEntry, unknown>[] = [
      columnHelper.display({
        id: "clock_in",
        header: t("clocking:started"),
        ...ColumnSizes.narrow,
        cell: ({ row }) => (
          <Stack direction={"horizontal"}>
            <Text className={"font-mono tabular-nums"}>
              {formatTime(row.original.datetime_clocked_in) || "—"}
            </Text>
            {row.original.is_edited && <Text className={"text-secondary"}>*</Text>}
          </Stack>
        ),
      }),
      columnHelper.display({
        id: "clock_in_comment",
        header: t("clocking:clock_in_comment"),
        ...ColumnSizes.wide,
        cell: ({ row }) => (
          <Text className={"truncate"}>{row.original.clock_in_comment || "—"}</Text>
        ),
      }),
      columnHelper.display({
        id: "clock_out",
        header: t("clocking:finished"),
        ...ColumnSizes.narrow,
        cell: ({ row }) => {
          const entry = row.original;
          if (entry.is_active) {
            return <Text className={"text-positive font-medium"}>{t("clocking:active")}</Text>;
          }
          return (
            <Text className={"tabular-nums"}>{formatTime(entry.datetime_clocked_out) || "—"}</Text>
          );
        },
      }),
      columnHelper.display({
        id: "clock_out_comment",
        header: t("clocking:clock_out_comment"),
        ...ColumnSizes.wide,
        cell: ({ row }) => (
          <Text className={"truncate"}>{row.original.clock_out_comment || "—"}</Text>
        ),
      }),
      columnHelper.display({
        id: "duration",
        header: t("common:duration"),
        ...ColumnSizes.standard,
        cell: ({ row }) => {
          const min = entryDurationMinutes(row.original);
          return <Text>{min !== null ? formatDurationMinutes(min) : "—"}</Text>;
        },
      }),
    ];

    return cols;
  }, [t]);

  const renderRowAction = useCallback(
    (row: Row<ApiTimeEntry>) => (
      <DropdownMenu>
        <DropdownMenuItem
          leftIcon={<FontAwesomeIcon icon={"fa-light fa-pen"} />}
          title={t("common:edit")}
          onClick={() => handleEdit(row.original)}
        />
        <DropdownMenuItem
          leftIcon={<FontAwesomeIcon icon={"fa-light fa-trash"} />}
          title={t("clocking:delete_entry") + "..."}
          onClick={() => handleDelete(row.original)}
          variant={"menuDanger"}
        />
      </DropdownMenu>
    ),
    [t, handleEdit, handleDelete]
  );

  return (
    <Modal
      {...props}
      title={`${t("clocking:adjust_time")} — ${format(date, "MMM d, yyyy")}`}
      withCloser={true}
      closeByEscEnabled={true}
      size={"xl"}
    >
      <Stack gap={"sm"}>
        {canEdit && (
          <Stack direction={"horizontal"} className={"justify-end"}>
            <Button
              variant={"secondary"}
              leftIcon={<FontAwesomeIcon icon={"fa-light fa-plus"} />}
              onClick={handleCreate}
            >
              {t("clocking:create_entry")}
            </Button>
          </Stack>
        )}
        {entries.length === 0 && !isLoading ? (
          <Text className={"text-secondary"}>{t("clocking:no_entries")}</Text>
        ) : (
          <Table
            data={entries}
            columns={columns}
            showSkeleton={isLoading}
            stickyFirstColumn={true}
            renderRowAction={canEdit ? renderRowAction : undefined}
          />
        )}
      </Stack>
    </Modal>
  );
};
